import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { AppLayout } from '@/components/layout/AppLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Clock, Download, History, Link, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useAccounts } from '@/lib/query/hooks';
import {
  useAllPredictionErrorEvaluationJobs,
  usePredictionErrorEvaluationJob,
  useStartPredictionErrorEvaluation,
  useDeletePredictionErrorEvaluation,
} from '@/lib/query/hooks/usePredictionErrorEvaluation';
import { downloadPredictionErrorEvaluationResult } from '@/lib/api/ds/prediction-error-evaluation';
import { useAuth, useAuthHeader } from '@/hooks/useAuth';
import { PredictionErrorEvaluationForm } from '@/components/ds/prediction-error-evaluation/PredictionErrorEvaluationForm';
import { JobStatusIndicator } from '@/components/ds/prediction-error-evaluation/JobStatusIndicator';
import { ParameterComparisonTable } from '@/components/ds/prediction-error-evaluation/ParameterComparisonTable';
import { ErrorHistogramChart } from '@/components/ds/prediction-error-evaluation/ErrorHistogramChart';
import { ProductGroupHistograms } from '@/components/ds/prediction-error-evaluation/ProductGroupHistograms';
import { JobHistoryTable } from '@/components/ds/prediction-error-evaluation/JobHistoryTable';
import { SalesTrendChart } from '@/components/ds/prediction-error-evaluation/SalesTrendChart';
import type {
  PredictionErrorEvaluationJob,
  StartPredictionErrorEvaluationRequest,
} from '@/lib/types/ds/prediction-error-evaluation';

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}時間${minutes}分${seconds}秒`;
  if (minutes > 0) return `${minutes}分${seconds}秒`;
  return `${seconds}秒`;
}

export function PredictionErrorEvaluationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { getAuthHeader } = useAuthHeader();

  // URLパラメータから初期値を復元
  const initialJobId = searchParams.get('job') ?? undefined;
  const initialAccountCode = searchParams.get('account') ?? '';
  const initialTab = initialJobId ? 'result' : 'new';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedAccountCode, setSelectedAccountCode] = useState(initialAccountCode);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(initialJobId);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showOnlyMyJobs, setShowOnlyMyJobs] = useState(true);
  const [selectedHistogramDate, setSelectedHistogramDate] = useState<string>('overall');

  const { data: accounts = [] } = useAccounts();
  const startMutation = useStartPredictionErrorEvaluation();
  const deleteMutation = useDeletePredictionErrorEvaluation();
  const [deletingJobs, setDeletingJobs] = useState<Set<string>>(new Set());
  const executedBy = showOnlyMyJobs && user?.email ? user.email : undefined;
  const { data: jobs = [] } = useAllPredictionErrorEvaluationJobs(executedBy);
  const { data: selectedJob } = usePredictionErrorEvaluationJob(selectedAccountCode, selectedJobId);

  const handleSubmit = async (
    accountCode: string,
    request: StartPredictionErrorEvaluationRequest,
  ) => {
    setSelectedAccountCode(accountCode);
    try {
      const response = await startMutation.mutateAsync({ accountCode, request });
      setSelectedJobId(response.job_id);
      setActiveTab('result');
      updateUrlParams(accountCode, response.job_id);
      toast.success('予測誤差評価ジョブを起動しました');
    } catch (error) {
      toast.error(
        `ジョブ起動に失敗しました: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const updateUrlParams = (accountCode: string, jobId: string) => {
    setSearchParams({ account: accountCode, job: jobId }, { replace: true });
  };

  const handleSelectJob = (job: PredictionErrorEvaluationJob) => {
    setSelectedAccountCode(job.account_code);
    setSelectedJobId(job.id);
    setActiveTab('result');
    updateUrlParams(job.account_code, job.id);
  };

  const handleCopyUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('account', selectedAccountCode);
    url.searchParams.set('job', selectedJobId ?? '');
    navigator.clipboard.writeText(url.toString()).then(() => {
      toast.success('URLをコピーしました');
    });
  };

  const handleDeleteJob = async (job: PredictionErrorEvaluationJob) => {
    if (deletingJobs.has(job.id)) return;
    setDeletingJobs((prev) => new Set(prev).add(job.id));
    try {
      await deleteMutation.mutateAsync({
        accountCode: job.account_code,
        jobId: job.id,
      });
      // 削除したジョブが選択中なら選択解除
      if (selectedJobId === job.id) {
        setSelectedJobId(undefined);
        setSelectedAccountCode('');
      }
      toast.success('ジョブを削除しました');
    } catch (error) {
      toast.error(
        `ジョブの削除に失敗しました: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setDeletingJobs((prev) => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
    }
  };

  const handleDownload = async () => {
    if (!selectedAccountCode || !selectedJobId) return;
    setIsDownloading(true);
    try {
      const authHeaders = getAuthHeader();
      await downloadPredictionErrorEvaluationResult(
        selectedAccountCode,
        selectedJobId,
        authHeaders,
      );
      toast.success('ダウンロードを開始しました');
    } catch (error) {
      toast.error(
        `ダウンロードに失敗しました: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const resultData = selectedJob?.result_data;

  return (
    <AppLayout title="予測誤差評価">
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">短期予測モデル 誤差評価・ヒストグラム可視化</h1>
          <p className="text-sm text-muted-foreground mt-1">
            パラメータを複数組み合わせてSageMaker予測を実行し、WMAPEで誤差を比較します。
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="new">
              <Play className="mr-1 h-4 w-4" />
              新規実行
            </TabsTrigger>
            <TabsTrigger value="result" disabled={!selectedJobId}>
              結果
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="mr-1 h-4 w-4" />
              履歴
            </TabsTrigger>
          </TabsList>

          {/* 新規実行タブ */}
          <TabsContent value="new" className="mt-4">
            <div className="max-w-2xl">
              <PredictionErrorEvaluationForm
                accounts={accounts}
                onSubmit={handleSubmit}
                isSubmitting={startMutation.isPending}
              />
            </div>
          </TabsContent>

          {/* 結果表示タブ */}
          <TabsContent value="result" className="mt-4 space-y-6">
            {selectedJob && (
              <>
                {/* ステータス */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">ジョブステータス</CardTitle>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                          <Link className="mr-1 h-4 w-4" />
                          URLをコピー
                        </Button>
                        {selectedJob.job_status === 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownload}
                            disabled={isDownloading}
                          >
                            <Download className="mr-1 h-4 w-4" />
                            {isDownloading ? 'ダウンロード中...' : '結果CSVダウンロード'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <JobStatusIndicator job={selectedJob} />
                  </CardContent>
                </Card>

                {/* 実行条件 */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">実行条件</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">アカウント: </span>
                        <span className="font-mono">{selectedJob.account_code}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">実行者: </span>
                        <span>{selectedJob.executed_by}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">基準日: </span>
                        <span>{(selectedJob.target_dates ?? []).join(', ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">評価日数: </span>
                        <span>{selectedJob.eval_days}日</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">対象SKU: </span>
                        {/* UUIDの一覧は30万件になり得るため、ファイル名か件数だけを出す */}
                        <span>
                          {selectedJob.target_skus_file_name
                            ? selectedJob.target_skus_file_name
                            : selectedJob.target_skus && selectedJob.target_skus.length > 0
                              ? `${selectedJob.target_skus.length}件を指定`
                              : '全SKU'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">学習期間: </span>
                        <span>{(selectedJob.train_terms ?? []).join(', ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">許容欠品率: </span>
                        <span>{(selectedJob.tolerances ?? []).join(', ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">使用データ数: </span>
                        <span>{(selectedJob.max_data_counts ?? []).join(', ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">組合せ数: </span>
                        <span>{selectedJob.total_combinations ?? '-'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">イベント補正: </span>
                        <span>{selectedJob.adjust_event ? 'あり' : 'なし'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">新商品SKU補正: </span>
                        <span>
                          {selectedJob.adjust_new_skus
                            ? `あり (min_train_term=${selectedJob.adjust_new_skus_min_train_term}, max_days_on_sales=${selectedJob.adjust_new_skus_max_days_on_sales})`
                            : 'なし'}
                        </span>
                      </div>
                      {selectedJob.product_group_type_name && (
                        <div>
                          <span className="text-muted-foreground">商品グループ: </span>
                          <span>
                            {selectedJob.product_group_type_name} (階層
                            {selectedJob.product_group_layer})
                          </span>
                        </div>
                      )}
                      {selectedJob.instance_type && (
                        <div>
                          <span className="text-muted-foreground">インスタンスタイプ: </span>
                          <span className="font-mono">{selectedJob.instance_type}</span>
                        </div>
                      )}
                    </div>
                    {selectedJob.execution_start_time && (
                      <div className="mt-3 flex items-center gap-1 text-sm text-muted-foreground border-t pt-3">
                        <Clock className="h-3.5 w-3.5" />
                        <span>
                          {new Date(selectedJob.execution_start_time).toLocaleString('ja-JP')}
                          {selectedJob.execution_end_time && (
                            <>
                              {' ~ '}
                              {new Date(selectedJob.execution_end_time).toLocaleString('ja-JP')}
                              {' ('}
                              {formatDuration(
                                new Date(selectedJob.execution_end_time).getTime() -
                                  new Date(selectedJob.execution_start_time).getTime(),
                              )}
                              {')'}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                    {resultData?.s3_paths && (
                      <div className="mt-3 space-y-1 border-t pt-3 text-sm">
                        <p className="font-medium text-muted-foreground">S3パス</p>
                        <div className="space-y-0.5 font-mono text-xs text-muted-foreground">
                          <p>中間ファイル: {resultData.s3_paths.intermediate_prefix}</p>
                          <p>結果ファイル: {resultData.s3_paths.result_file}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* 結果データ（completed時のみ） */}
                {resultData && (
                  <>
                    {/* 最適パラメータ */}
                    {resultData.best_params && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">最適パラメータ（WMAPE最小）</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex gap-6 text-sm">
                            {resultData.best_params.target_date && (
                              <div>
                                <span className="text-muted-foreground">基準日: </span>
                                <span className="font-semibold">
                                  {resultData.best_params.target_date}
                                </span>
                              </div>
                            )}
                            <div>
                              <span className="text-muted-foreground">train_term: </span>
                              <span className="font-semibold">
                                {resultData.best_params.train_term}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">tolerance: </span>
                              <span className="font-semibold">
                                {resultData.best_params.tolerance}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">max_data_count: </span>
                              <span className="font-semibold">
                                {resultData.best_params.max_data_count}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* target_date別WMAPEサマリー（複数日付の場合のみ） */}
                    {(() => {
                      const dates = [
                        ...new Set(
                          resultData.parameter_comparison.map((r) => r.target_date).filter(Boolean),
                        ),
                      ];
                      if (dates.length < 2) return null;
                      const summaries = dates.map((date) => {
                        const rows = resultData.parameter_comparison.filter(
                          (r) => r.target_date === date,
                        );
                        const wmapes = rows.map((r) => r.wmape);
                        const min = Math.min(...wmapes);
                        const avg = wmapes.reduce((a, b) => a + b, 0) / wmapes.length;
                        const best = rows.find((r) => r.wmape === min);
                        return { date, min, avg, best };
                      });
                      return (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base">基準日別 WMAPEサマリー</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="rounded-md border">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-muted/50">
                                    <th className="px-4 py-2 text-left font-medium">基準日</th>
                                    <th className="px-4 py-2 text-right font-medium">最小WMAPE</th>
                                    <th className="px-4 py-2 text-right font-medium">平均WMAPE</th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      最適パラメータ
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {summaries.map((s) => (
                                    <tr key={s.date} className="border-b last:border-0">
                                      <td className="px-4 py-2">{s.date}</td>
                                      <td className="px-4 py-2 text-right font-semibold text-green-600">
                                        {(s.min * 100).toFixed(2)}%
                                      </td>
                                      <td className="px-4 py-2 text-right">
                                        {(s.avg * 100).toFixed(2)}%
                                      </td>
                                      <td className="px-4 py-2 text-muted-foreground">
                                        {s.best &&
                                          `tt=${s.best.train_term} tl=${s.best.tolerance} mdc=${s.best.max_data_count}`}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })()}

                    {/* WMAPE比較テーブル */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">パラメータ比較表（WMAPE昇順）</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ParameterComparisonTable data={resultData.parameter_comparison} />
                      </CardContent>
                    </Card>

                    {/* ヒストグラム */}
                    {resultData.histogram_data && (
                      <>
                        {/* 日付セレクター（複数日付 + by_dateが存在する場合のみ） */}
                        {resultData.by_date && resultData.by_date.length > 0 && (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-base">
                                表示対象（ヒストグラム・累積推移）
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  variant={
                                    selectedHistogramDate === 'overall' ? 'default' : 'outline'
                                  }
                                  size="sm"
                                  onClick={() => setSelectedHistogramDate('overall')}
                                >
                                  全体最適
                                </Button>
                                {resultData.by_date.map((entry) => (
                                  <Button
                                    key={entry.target_date}
                                    variant={
                                      selectedHistogramDate === entry.target_date
                                        ? 'default'
                                        : 'outline'
                                    }
                                    size="sm"
                                    onClick={() => setSelectedHistogramDate(entry.target_date)}
                                  >
                                    {entry.target_date}
                                  </Button>
                                ))}
                              </div>
                              {selectedHistogramDate !== 'overall' &&
                                (() => {
                                  const entry = resultData.by_date?.find(
                                    (e) => e.target_date === selectedHistogramDate,
                                  );
                                  if (!entry) return null;
                                  return (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      最適パラメータ: train_term={entry.train_term}, tolerance=
                                      {entry.tolerance}, max_data_count={entry.max_data_count}
                                    </p>
                                  );
                                })()}
                            </CardContent>
                          </Card>
                        )}

                        {/* ヒストグラム表示 */}
                        {(() => {
                          const histData =
                            selectedHistogramDate === 'overall' || !resultData.by_date
                              ? resultData.histogram_data
                              : (resultData.by_date.find(
                                  (e) => e.target_date === selectedHistogramDate,
                                )?.histogram_data ?? resultData.histogram_data);

                          const titlePrefix =
                            selectedHistogramDate === 'overall' ? '' : `${selectedHistogramDate} `;

                          return (
                            <>
                              {selectedHistogramDate === 'overall' && resultData.best_params && (
                                <p className="text-sm text-muted-foreground">
                                  以下のヒストグラムは最適パラメータ
                                  {resultData.best_params.target_date &&
                                    `（基準日: ${resultData.best_params.target_date}）`}
                                  の結果に基づいています。
                                </p>
                              )}
                              <ErrorHistogramChart
                                title={`${titlePrefix}誤差分布（全体 - ログスケール）`}
                                data={histData.overall}
                                useLogScale
                              />
                              <ErrorHistogramChart
                                title={`${titlePrefix}誤差分布（98%区間 - ログスケール）`}
                                data={histData.overall_98pct}
                                useLogScale
                                showPercentileLines={false}
                              />
                              {histData.by_product_group.length > 0 && (
                                <Card>
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-base">
                                      {titlePrefix}商品グループ別誤差分布
                                    </CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <ProductGroupHistograms data={histData.by_product_group} />
                                  </CardContent>
                                </Card>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}

                    {/* 販売数 実績 vs 予測 累積推移 */}
                    {resultData.sales_trend && (
                      <SalesTrendChart
                        data={resultData.sales_trend}
                        selectedTargetDate={
                          selectedHistogramDate === 'overall'
                            ? resultData.best_params?.target_date
                            : selectedHistogramDate
                        }
                      />
                    )}
                  </>
                )}
              </>
            )}

            {!selectedJob && (
              <p className="text-sm text-muted-foreground">
                ジョブを選択するか、新規実行してください。
              </p>
            )}
          </TabsContent>

          {/* 履歴タブ */}
          <TabsContent value="history" className="mt-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-only-my-jobs"
                checked={showOnlyMyJobs}
                onCheckedChange={(checked) => setShowOnlyMyJobs(checked === true)}
              />
              <label
                htmlFor="show-only-my-jobs"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                自分のジョブのみ表示
              </label>
            </div>
            {jobs.length > 0 ? (
              <JobHistoryTable
                jobs={jobs}
                onSelect={handleSelectJob}
                onDelete={handleDeleteJob}
                deletingJobs={deletingJobs}
              />
            ) : (
              <p className="text-sm text-muted-foreground">ジョブ履歴がありません。</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
